import type { CouponValidateView } from '../api/generated/models/CouponValidateView';

/**
 * Single dictionary for coupon rejection codes: `reason` from
 * GET /coupon/validate and `errorCode` from a 400 of POST /payment carry the
 * same values. Maps a code to its i18n key; unknown codes fall back to the
 * generic "invalid" text.
 */
const COUPON_REASON_I18N: Record<string, string> = {
  coupon_not_found: 'affiliate.coupon_reason_not_found',
  coupon_inactive: 'affiliate.coupon_reason_inactive',
  coupon_not_started: 'affiliate.coupon_reason_not_started',
  coupon_expired: 'affiliate.coupon_reason_expired',
  coupon_exhausted: 'affiliate.coupon_reason_exhausted',
  coupon_product_not_eligible: 'affiliate.coupon_reason_product_not_eligible',
  coupon_not_for_this_customer: 'affiliate.coupon_reason_not_for_this_customer',
  coupon_currency_mismatch: 'affiliate.coupon_reason_currency_mismatch',
};

export const isCouponReason = (code: unknown): code is string =>
  typeof code === 'string' && code in COUPON_REASON_I18N;

export const couponReasonI18nKey = (code: string | null | undefined): string =>
  (code && COUPON_REASON_I18N[code]) || 'affiliate.coupon_error_invalid';

/** Partner coupon caps - backend config mirrored here for hints / input limits only. */
export const PARTNER_COUPON_MAX_PERCENT = 20;
export const PARTNER_COUPON_MAX_MONTHS = 6;

/** A successfully validated coupon, as returned by GET /coupon/validate. */
export interface AppliedCoupon {
  code: string;
  discountType: 'percent' | 'fixed';
  /** Percent for `percent`, cents for `fixed`. */
  discountValue: number;
  currency: string | null;
  duration: 'repeating' | 'forever';
  durationInMonths: number | null;
}

/**
 * Discount duration text shared by checkout and the partner cabinet:
 * `forever` → lifetime; `repeating` with 1 month → first invoice only,
 * otherwise "N months".
 */
export const couponDurationLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  duration: string | null | undefined,
  durationInMonths: number | null | undefined
): string => {
  if (duration === 'forever') return t('affiliate.coupon_duration_forever');
  const months = durationInMonths ?? 0;
  return months === 1
    ? t('affiliate.coupon_duration_once')
    : t('affiliate.coupon_duration_repeating', { months });
};

/** Builds the applied-coupon state from a `valid: true` validate response, or null. */
export const appliedCouponFromView = (view: CouponValidateView): AppliedCoupon | null => {
  if (!view.valid || !view.code || typeof view.discountValue !== 'number') return null;
  if (view.discountType !== 'percent' && view.discountType !== 'fixed') return null;
  if (view.duration !== 'repeating' && view.duration !== 'forever') return null;
  return {
    code: view.code,
    discountType: view.discountType,
    discountValue: view.discountValue,
    currency: view.currency ?? null,
    duration: view.duration,
    durationInMonths: view.durationInMonths ?? null,
  };
};

/**
 * Display-only price after the coupon (cents). Stripe computes the real
 * invoice amount; this mirrors its rule on the same netto base the UI shows.
 */
export const applyCouponDiscount = (cents: number, coupon: AppliedCoupon | null | undefined): number => {
  if (!coupon) return cents;
  if (coupon.discountType === 'percent') {
    return Math.round((cents * (100 - coupon.discountValue)) / 100);
  }
  return Math.max(0, cents - coupon.discountValue);
};
